import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase/config';
import { 
  ArrowRight, 
  Smartphone, 
  Globe, 
  Users, 
  QrCode, 
  CheckCircle,
  Star,
  Download,
  Zap
} from 'lucide-react';
import { cn } from '../utils';
import SocialLoginButton from '../components/SocialLoginButtons';
import LanguageSelector from '../components/LanguageSelector';

const LandingPage: React.FC = () => {
  console.log('🚀 LandingPage component rendering!');
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, loginWithGoogle, loading } = useAuth();

  console.log('🌐 LandingPage - Current language:', i18n.language);
  console.log('🌐 LandingPage - Available languages:', i18n.languages);
  console.log('🌐 LandingPage - Is ready:', i18n.isInitialized);

  console.log('🏠 LandingPage - isAuthenticated:', isAuthenticated);
  console.log('🏠 LandingPage - user:', user);
  console.log('🏠 LandingPage - loading:', loading);

  const handleGoogleLogin = async () => {
    console.log('🔘 Google login button clicked! (LandingPage)');
    console.log('🔍 Testing basic functionality...');
    
    try {
      console.log('🔄 Starting Google login process...');
      
      // 기본 테스트: Firebase Auth 객체 확인
      console.log('🔍 Firebase Auth object:', auth);
      console.log('🔍 Current user:', auth.currentUser);
      
      await loginWithGoogle();
    } catch (e: any) {
      const c = e?.code || '';
      console.log('❌ Google login error caught:', c);
      console.log('❌ Full error:', e);
      
      // 폴백 대상 에러는 무시 (이미 signInWithRedirect 실행됨)
      if (
        c === 'auth/popup-blocked' ||
        c === 'auth/popup-closed-by-user' ||
        c === 'auth/cancelled-popup-request' ||
        c === 'auth/web-storage-unsupported'
      ) {
        console.log('🔄 Ignoring fallback error');
        return;
      }
      console.error('Google 로그인 실패:', e);
      // 사용자에게 에러 메시지 표시 (선택사항)
      alert(`Google 로그인 실패: ${e.message || '알 수 없는 오류'}`);
    }
  };

  const features = [
    {
      icon: <QrCode className="h-6 w-6" />,
      title: t('landing.features.qr.title', 'QR Code Generation'),
      description: t('landing.features.qr.description', 'Generate custom QR codes for instant sharing of your digital business card')
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: t('landing.features.web.title', 'Web Dashboard'),
      description: t('landing.features.web.description', 'Manage your cards, view analytics, and edit profiles from any device')
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: t('landing.features.network.title', 'Network Management'),
      description: t('landing.features.network.description', 'Organize and manage your professional connections efficiently')
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: t('landing.features.ai.title', 'AI-Powered'),
      description: t('landing.features.ai.description', 'AI-generated introductions and service descriptions for your business')
    }
  ];

  const plans = [
    {
      name: t('landing.plans.free.name', 'Free'),
      price: t('landing.plans.free.price', 'Free'),
      features: [
        t('landing.plans.free.feature1', '1 Digital Business Card'),
        t('landing.plans.free.feature2', 'Basic QR Code'),
        t('landing.plans.free.feature3', 'Web Profile'),
        t('landing.plans.free.feature4', 'Email Support')
      ],
      popular: false,
      cta: t('landing.plans.free.cta', 'Get Started Free')
    },
    {
      name: t('landing.plans.premium.name', 'Premium'),
      price: t('landing.plans.premium.price', '$9.99/month'),
      features: [
        t('landing.plans.premium.feature1', 'Unlimited Digital Cards'),
        t('landing.plans.premium.feature2', 'Custom QR Codes'),
        t('landing.plans.premium.feature3', 'Analytics & Insights'),
        t('landing.plans.premium.feature4', 'Priority Support'),
        t('landing.plans.premium.feature5', 'Custom Domain'),
        t('landing.plans.premium.feature6', 'AI Content Generation')
      ],
      popular: true,
      cta: t('landing.plans.premium.cta', 'Start Premium Trial')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <span className="text-xl font-bold text-gray-900">ScanVCF</span>
            </div>
            <div className="flex items-center space-x-4">
              {/* 현재 언어 표시 (테스트용) */}
              <span className="text-xs text-gray-500 hidden sm:block">
                {t('common.login', 'Language')}: {i18n.language}
              </span>
              
              {/* 언어 선택기 */}
              <LanguageSelector variant="button" />
              
              {isAuthenticated ? (
                <>
                  <span className="text-gray-600">
                    {t('navigation.welcome', 'Hello')}, {user?.displayName || user?.email}!
                  </span>
                  <Link
                    to="/dashboard"
                    className="btn-primary"
                  >
                    {t('navigation.dashboard', 'Dashboard')}
                  </Link>
                </>
              ) : (
                <>
                  <SocialLoginButton
                    provider="google"
                    onClick={handleGoogleLogin}
                    loading={loading}
                    size="lg"
                    variant="long-button"
                  />
                  <Link
                    to="/login"
                    className="text-gray-600 hover:text-gray-900 font-medium"
                  >
                    {t('auth.login', 'Login')}
                  </Link>
                  <Link
                    to="/register"
                    className="btn-primary"
                  >
                    {t('auth.register', 'Sign Up')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('common.digitalBusinessCard', 'Digital Business Cards')}
              <span className="text-primary-600 block">{t('common.save', 'Made Simple')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {t('landing.hero.subtitle', 'Create, share, and manage your professional digital business cards with QR codes. Connect with your network effortlessly.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/register"
                className="btn-primary inline-flex items-center justify-center px-8 py-3 text-lg font-medium"
              >
                {t('auth.signup', 'Get Started Free')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <SocialLoginButton
                provider="google"
                onClick={handleGoogleLogin}
                loading={loading}
                size="lg"
                variant="long-button"
              />
              <Link
                to="/download"
                className="btn-secondary inline-flex items-center justify-center px-8 py-3 text-lg font-medium"
              >
                <Download className="mr-2 h-5 w-5" />
                {t('landing.hero.downloadApp', 'Download App')}
              </Link>
            </div>
            
            {/* App Store Badges */}
            <div className="flex justify-center space-x-4">
              <img 
                src="/images/app-store-badge.png" 
                alt="Download on App Store"
                className="h-12"
              />
              <img 
                src="/images/google-play-badge.png" 
                alt="Get it on Google Play"
                className="h-12"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.features.title', 'Everything you need for digital networking')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('landing.features.subtitle', 'Powerful features to help you create and manage your professional presence')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <div className="text-primary-600">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.pricing.title', 'Choose Your Plan')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('landing.pricing.subtitle', 'Start free and upgrade when you need more features')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={cn(
                  "bg-white rounded-lg shadow-lg p-8 relative",
                  plan.popular && "ring-2 ring-primary-500"
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      {t('landing.pricing.mostPopular', 'Most Popular')}
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold text-gray-900 mb-6">
                    {plan.price}
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to={plan.name === 'Free' ? '/register' : '/register?plan=premium'}
                    className={cn(
                      "w-full inline-flex items-center justify-center px-6 py-3 text-lg font-medium rounded-lg transition-colors",
                      plan.popular 
                        ? "bg-primary-600 text-white hover:bg-primary-700" 
                        : "bg-gray-200 text-gray-900 hover:bg-gray-300"
                    )}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('landing.cta.title', 'Ready to go digital?')}
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            {t('landing.cta.subtitle', 'Join thousands of professionals who have already made the switch to digital business cards')}
          </p>
          <Link
            to="/register"
            className="bg-white text-primary-600 hover:bg-gray-100 inline-flex items-center justify-center px-8 py-3 text-lg font-medium rounded-lg transition-colors"
          >
            {t('landing.cta.button', 'Start Creating Your Card')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">S</span>
                </div>
                <span className="text-xl font-bold">ScanVCF</span>
              </div>
              <p className="text-gray-400">
                {t('landing.footer.description', 'Create and share professional digital business cards with QR codes.')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.product', 'Product')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/features" className="hover:text-white">{t('landing.footer.features', 'Features')}</Link></li>
                <li><Link to="/pricing" className="hover:text-white">{t('landing.footer.pricing', 'Pricing')}</Link></li>
                <li><Link to="/download" className="hover:text-white">{t('landing.footer.download', 'Download')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.support', 'Support')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/help" className="hover:text-white">{t('landing.footer.help', 'Help Center')}</Link></li>
                <li><Link to="/contact" className="hover:text-white">{t('landing.footer.contact', 'Contact Us')}</Link></li>
                <li><Link to="/status" className="hover:text-white">{t('landing.footer.status', 'Status')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.legal', 'Legal')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/privacy-policy" className="hover:text-white">{t('landing.footer.privacy', 'Privacy Policy')}</a></li>
                <li><a href="/terms" className="hover:text-white">{t('landing.footer.terms', 'Terms of Service')}</a></li>
                <li><a href="/delete-account" className="hover:text-white">Delete Account</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 ScanVCF. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 